/** Public receiver metadata only. Never include an activation token or media URL. */
export type NativeSourceCapability = 'torrent-v1' | 'nzb-direct-v1';
export type NativeFile = { index: number; label: string; size: number; estimatedSize?: true };
type PreparationIdentity = { sessionId: string; revision: number; title: string; expiresAt: number };
export type NativePreparation = PreparationIdentity & (
  | { phase: 'preparing' | 'activating' }
  | { phase: 'choosing'; page: number; totalFiles: number; files: NativeFile[] }
);
export type NativeFileSelection =
  | { action: 'file'; fileIndex: number }
  | { action: 'page'; page: number };

export function parseNativeCapabilities(value: unknown): NativeSourceCapability[] {
  return Array.isArray(value) && value.length <= 2 && new Set(value).size === value.length &&
    value.every(item => item === 'torrent-v1' || item === 'nzb-direct-v1') ? [...value] : [];
}

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function integer(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
}
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Copy only the bounded public page. Expiry is handled separately by the connection. */
export function parseNativePreparation(value: unknown): NativePreparation | null {
  if (!record(value)) return null;
  const { sessionId, revision, title, expiresAt, phase } = value;
  if (typeof sessionId !== 'string' || !uuid.test(sessionId) ||
      !integer(revision, 0, Number.MAX_SAFE_INTEGER) ||
      typeof title !== 'string' || title.length > 300 ||
      !integer(expiresAt, 1, Number.MAX_SAFE_INTEGER)) return null;
  const identity = { sessionId, revision, title, expiresAt };
  const fields = ['sessionId', 'revision', 'title', 'expiresAt', 'phase'];
  if (phase === 'preparing' || phase === 'activating') {
    return Object.keys(value).every(key => fields.includes(key)) ? { ...identity, phase } : null;
  }
  if (phase !== 'choosing' || !Object.keys(value).every(key => [...fields, 'page', 'totalFiles', 'files'].includes(key))) return null;
  const { page, totalFiles, files } = value;
  if (!integer(page, 0, 79) || !integer(totalFiles, 1, 2000) || !Array.isArray(files)) return null;
  const count = Math.min(25, totalFiles - page * 25);
  if (count <= 0 || files.length !== count) return null;
  const copied: NativeFile[] = [];
  for (const file of files) {
    if (!record(file) || !Object.keys(file).every(key => ['index', 'label', 'size', 'estimatedSize'].includes(key)) ||
        (file.estimatedSize !== undefined && file.estimatedSize !== true) ||
        !integer(file.index, 0, 1999) || !integer(file.size, file.estimatedSize ? 0 : 1, 1_000_000_000_000) ||
        typeof file.label !== 'string' || !file.label.length || file.label.length > 180 || /[\p{Cc}\p{Cf}]/u.test(file.label)) return null;
    copied.push({ index: file.index, label: file.label, size: file.size, ...(file.estimatedSize ? { estimatedSize: true as const } : {}) });
  }
  if (new Set(copied.map(file => file.index)).size !== count) return null;
  return { ...identity, phase, page, totalFiles, files: copied };
}
